import ReactDOM from 'react-dom';
import React, { Component, ReactNode } from 'react';
import { omit } from 'lodash';

export const {
  Provider: FrameContextProvider,
  Consumer: FrameContextConsumer,
} = React.createContext({
  document: typeof document !== 'undefined' ? document : null,
  window: typeof window !== 'undefined' ? window : null,
});

type FrameProps = {
  style?: object;
  children: ReactNode;
  className?: string;
  initialContent?: string;
  title: string;
};

class Frame extends Component<FrameProps, any> {
  node: HTMLIFrameElement | null = null;
  _setInitialContent: boolean | null = null;
  _isMounted = false;
  static defaultProps = {
    style: {},
    initialContent:
      '<!DOCTYPE html><html><head></head><body><div class="frame-root"></div></body></html>',
  };

  componentDidMount() {
    this._isMounted = true;
    const doc = this.getDoc();

    if (doc && doc.readyState === 'complete') {
      this.forceUpdate();
    } else {
      if (this.node) {
        this.node.addEventListener('load', this.handleLoad);
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;

    if (this.node) {
      this.node.removeEventListener('load', this.handleLoad);
    }
  }

  getDoc() {
    return this.node && this.node.contentDocument;
  }

  getMountTarget() {
    const doc = this.getDoc();
    return doc && doc.body.children[0];
  }

  handleLoad = () => {
    this.forceUpdate();
  };

  renderFrameContents() {
    if (!this._isMounted) {
      return null;
    }

    const doc = this.getDoc();

    if (doc && doc.querySelector('.frame-content') === null) {
      this._setInitialContent = false;
    }

    const win = (doc && doc.defaultView) || (doc && (doc as any).parentView);
    const initialRender = !this._setInitialContent;
    const contents = (
      <FrameContextProvider
        value={{
          document: doc,
          window: win,
        }}
      >
        <div className="frame-content">{this.props.children}</div>
      </FrameContextProvider>
    );

    if (initialRender && doc) {
      doc.open('text/html', 'replace');
      doc.write(this.props.initialContent as string);
      doc.close();
      this._setInitialContent = true;
    }

    const mountTarget = this.getMountTarget();
    return doc && mountTarget
      ? [ReactDOM.createPortal(contents, mountTarget)]
      : [];
  }

  render() {
    const childProps = omit(this.props, ['head', 'initialContent', 'children']);
    return (
      <iframe
        {...childProps}
        ref={node => (this.node = node)}
        title={this.props.title}
      >
        {this.renderFrameContents()}
      </iframe>
    );
  }
}

export default Frame;
